import { GpxSummary } from '../../types'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Separator } from '../ui/separator'
import { MapPin, Mountain, Route, Clock } from 'lucide-react'

interface GpxSummaryProps {
  gpxData: GpxSummary
  className?: string
}

export function GpxSummaryDisplay({ gpxData, className }: GpxSummaryProps) {
  const {
    totalDistance,
    totalElevationGain,
    totalElevationLoss,
    trackPoints,
    startPoint,
    endPoint
  } = gpxData

  const formatCoordinate = (lat: number, lon: number) => {
    const latStr = `${Math.abs(lat).toFixed(4)}°${lat >= 0 ? 'N' : 'S'}`
    const lonStr = `${Math.abs(lon).toFixed(4)}°${lon >= 0 ? 'E' : 'W'}`
    return `${latStr}, ${lonStr}`
  }

  const estimatedTime = Math.round((totalDistance / 8) * 60) // Rough estimate at 8km/h pace

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Route className="h-5 w-5" />
          GPX Course Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Distance and Elevation Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Distance</span>
              <Badge variant="secondary">
                {totalDistance.toFixed(2)} km
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 flex items-center gap-1">
                <Mountain className="h-3 w-3" />
                Elevation Gain
              </span>
              <span className="text-sm font-medium">
                +{totalElevationGain}m
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 flex items-center gap-1">
                <Mountain className="h-3 w-3 rotate-180" />
                Elevation Loss
              </span>
              <span className="text-sm font-medium">
                -{totalElevationLoss}m
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Track Points</span>
              <span className="text-sm font-medium">
                {trackPoints.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Est. Time
              </span>
              <span className="text-sm font-medium">
                {Math.floor(estimatedTime / 60)}h {estimatedTime % 60}m
              </span>
            </div>
          </div>
        </div>

        <Separator />

        {/* Start and End Coordinates */}
        <div className="space-y-3">
          <div>
            <div className="flex items-center gap-1 mb-1">
              <MapPin className="h-3 w-3 text-green-600" />
              <span className="text-xs font-medium text-green-600 uppercase tracking-wide">
                Start Point
              </span>
            </div>
            <p className="text-xs text-gray-600 font-mono">
              {formatCoordinate(startPoint.lat, startPoint.lon)}
            </p>
            {startPoint.ele && (
              <p className="text-xs text-gray-500">
                Elevation: {Math.round(startPoint.ele)}m
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center gap-1 mb-1">
              <MapPin className="h-3 w-3 text-red-600" />
              <span className="text-xs font-medium text-red-600 uppercase tracking-wide">
                End Point
              </span>
            </div>
            <p className="text-xs text-gray-600 font-mono">
              {formatCoordinate(endPoint.lat, endPoint.lon)}
            </p>
            {endPoint.ele && (
              <p className="text-xs text-gray-500">
                Elevation: {Math.round(endPoint.ele)}m
              </p>
            )}
          </div>
        </div>

        {/* Data Quality Indicators */}
        <div className="pt-2">
          <div className="flex flex-wrap gap-1">
            <Badge variant="outline" className="text-xs">
              {(totalDistance / trackPoints * 1000).toFixed(1)}m avg spacing
            </Badge>
            {totalElevationGain > 0 && (
              <Badge variant="outline" className="text-xs">
                Elevation data
              </Badge>
            )}
            {startPoint.time && (
              <Badge variant="outline" className="text-xs">
                Time stamps
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}